
import React, { useEffect, useState, useRef, useCallback } from 'react';
import DataTable from 'react-data-table-component';
import { Button, Form, Modal, Spinner, Alert } from 'react-bootstrap';
import { apiGet, apiPost, apiPut, apiDelete } from '../services/api';
import { toast } from 'react-toastify';
import logger from '../utils/logger';

const speak = (text, rate = 0.9) => {
  const synth = window.speechSynthesis;
  synth.cancel(); 
  const voices = synth.getVoices();
  const femaleVoice =
    voices.find(v =>
      v.lang.toLowerCase().includes("en") &&
      (v.name.toLowerCase().includes("zira") ||
        v.name.toLowerCase().includes("anya") ||
        v.name.toLowerCase().includes("female") ||
        v.name.toLowerCase().includes("susan"))
    ) || voices.find(v => v.lang === "en-US");

  const utter = new SpeechSynthesisUtterance(text);
  utter.voice = femaleVoice;
  utter.lang = "en-US";
  utter.rate = rate;
  synth.speak(utter);
};

if (window.speechSynthesis.onvoiceschanged !== undefined) {
  window.speechSynthesis.onvoiceschanged = () => {
    window.speechSynthesis.getVoices();
  };
}
const ConfirmToast = ({ message, onConfirm, onCancel }) => {
  return (
    <div  style={{ maxWidth: '600px' }}>
  <div className="mb-3">
    <h6 className="mb-0 text-center meassage-text">{message}</h6>
  </div>
  <div className="d-flex justify-content-center gap-2">

   
    <Button  className='yes-button' size="sm" onClick={onConfirm}>
      Yes
    </Button>
     <Button  className='No-button' size="sm" onClick={onCancel}>
      No
    </Button>
  </div>
</div>

  );
};

const Category = () => {
  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState({ categoryName: '' });
  const [editId, setEditId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const inputRef = useRef();
  const hasSpokenCloseRef = useRef(false);
const [isDeleteConfirmationActive, setIsDeleteConfirmationActive] = useState(false);
  const BASE_ENDPOINT = '/Category';

  useEffect(() => {
    const timer = setTimeout(() => {
      speak("Welcome to Category Management. You can add, edit or delete categories here.");
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  const fetchCategories = useCallback(async () => {
    setIsLoading(true);
    try {
      logger.debug('Fetching categories...');
      const response = await apiGet(`${BASE_ENDPOINT}/GetAll`);
      if (response?.length) {
        const sortedData = [...response].sort((a, b) => b.categoryID - a.categoryID);
        setCategories(sortedData);
        logger.debug('Categories fetched:', sortedData);
      } else {
        setCategories([]);
        logger.debug('No categories found.');
      }
    } catch (error) {
      logger.error('Failed to fetch categories:', error);
      toast.error(
        <div>
          <strong>Failed to load categories</strong>
          <div>{error.message}</div>
        </div>,
        { autoClose: 7000 }
      );
      speak("Failed to load categories. Please try again.");
      setCategories([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    if (isModalOpen && inputRef.current) {
      inputRef.current.focus();
      speak(editId ? "Editing category." : "Adding new category.");
    }
  }, [isModalOpen, editId]);

  const openModal = (data = { categoryName: '' }) => {
    setFormData(data);
    setIsModalOpen(true);
    hasSpokenCloseRef.current = false;
  };

  const closeModal = () => {
    setFormData({ categoryName: '' });
    setEditId(null);
    setIsModalOpen(false);
    
    if (!hasSpokenCloseRef.current) {
      speak("Form closed.");
      hasSpokenCloseRef.current = true;
    }
  };

  const sanitizeCategoryName = (input) => {
    return input
      .replace(/[^a-zA-Z\s]/g, '') 
      .replace(/\s+/g, ' ') 
      .trim();
  };

  const handleSpeechInput = () => {
    if (!window.SpeechRecognition && !window.webkitSpeechRecognition) {
      alert("Speech Recognition is not supported in this browser.");
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.lang = "en-US";
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      setIsListening(true);
      speak("Listening...");
    };

    recognition.onerror = (event) => {
      setIsListening(false);
      speak("Sorry, I didn't catch that. Please try again.");
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognition.onresult = (event) => {
      let transcript = event.results[0][0].transcript.trim();
      transcript = sanitizeCategoryName(transcript);
      setFormData({ categoryName: transcript });
      speak(`You said: ${transcript}`);
    };

    recognition.start();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let trimmedName = sanitizeCategoryName(formData.categoryName?.trim());

    if (!trimmedName) {
      const msg = 'Category name is required!';
      toast.error(msg);
      speak(msg);
      return;
    }

    try {
      setIsLoading(true);
      const payload = { categoryName: trimmedName };

      if (editId) {
        await apiPut(`${BASE_ENDPOINT}/${editId}`, { ...payload, categoryID: editId });
        const msg = `Category Updated Successfully!`;
        toast.success(
          <div>
            <strong>{msg}</strong>
          </div>,
          { autoClose: 5000 }
        );
        speak(msg);
      } else {
        await apiPost(`${BASE_ENDPOINT}/Create`, payload);
        const msg = `Category Added Successfully! `;
        toast.success(
          <div>
            <strong>{msg}</strong>
          </div>,
          { autoClose: 5000 }
        );
        speak(msg);
      }

      await fetchCategories();
      closeModal();
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleApiError = (error) => {
    logger.error('API Error:', error);
    const statusCode = error?.response?.status;
    const apiMessage = error?.response?.data?.status;

    const errorMessages = {
      400: apiMessage || 'Invalid category data',
      404: 'Category not found',
      409: 'Category name already exists',
      500: 'Internal server error'
    };

    const errorMessage = errorMessages[statusCode] || 'Something went wrong';
    const fullMessage = `Operation Failed. ${errorMessage}`;
    
    toast.error(
      <div>
        <strong>{fullMessage}</strong>
      </div>,
      { autoClose: 7000 }
    );
    speak(fullMessage);
  };

  const handleEdit = (row) => {
    logger.debug('Editing category:', row);
    setEditId(row.categoryID);
    openModal({ categoryName: row.categoryName });
  };

  const handleDelete = (id) => {
     if (isDeleteConfirmationActive) return;
    const categoryToDelete = categories.find(c => c.categoryID === id);
    const confirmMsg = `Are you sure you want to delete the category`;
 setIsDeleteConfirmationActive(true);
    speak(confirmMsg);

    toast.info(
      <ConfirmToast
        message={confirmMsg}
        onConfirm={async () => {
          toast.dismiss();
          try {
            setIsLoading(true);
            await apiDelete(`${BASE_ENDPOINT}/${id}`);
            const msg = 'Category Deleted Successfully!';
            toast.success(<strong>{msg}</strong>, {
          autoClose: 5000,
          icon: false, // ✅ disables icon for success toast
        });
            speak(msg);
            await fetchCategories();
          } catch (error) {
            handleApiError(error);
          } finally {
            setIsLoading(false);
            setIsDeleteConfirmationActive(false);
          }
        }}
        onCancel={() => {
          toast.dismiss();
          speak("Delete cancelled.");
          setIsDeleteConfirmationActive(false);
        }}
      />,
      {
         position: "top-center",
    autoClose: false,
    closeOnClick: false,
    draggable: false,
    closeButton: false,
    icon: false, 
   
    className: 'custom-toast', 
    bodyClassName: 'custom-toast-body', 
    hideProgressBar: true,
      }
    );
  };

  const columns = [
    {
      name: 'Category Name',
      selector: row => row.categoryName,
      sortable: true,
         width: '600px',
   center:true,
      cell: row => <span className="text-capitalize">{row.categoryName}</span>,
        style: {
    borderRight: '1px solid #e5e8ebff!important', 
  },
    },
    {
      name: 'Actions',
      cell: (row) => (
        <div className="d-flex gap-2">
          <Button 
            variant="outline-warning custompadding" 
            size="sm me-2" 
            onClick={() => handleEdit(row)}
          >
            <i className="bi bi-pencil"></i>
          </Button>
          <Button 
            variant="outline-danger custompadding" 
            size="sm me-2" 
            onClick={() => handleDelete(row.categoryID)}
            onFocus={() => speak(`Delete ${row.categoryName} category`)}
            disabled={isDeleteConfirmationActive}
          >
            <i className="bi bi-trash"></i>
          </Button>
        </div>
      ),
    width: '450px',
     center:true,
    },
  ];

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0 page-heading">Category</h2>
        <Button 
          variant="btn-primary" 
          className="btn-fontweight" 
          onClick={() => openModal()} 
          disabled={isLoading}
        >
          {isLoading ? <Spinner size="sm" /> : 'Add Category'}
        </Button>
      </div>

      {isLoading && !categories.length ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="btn-primary" />
          <p className="mt-2">Loading categories...</p>
        </div>
      ) : (
        <div className="card shadow-sm rounded-4 py-4 px-0 border-0 bg-white mb-custom">
          <DataTable
            columns={columns}
          
              data={categories.map((dept, idx) => ({
    ...dept,
    _rowIndex: idx, 
  }))}
            pagination
      
             className="custom-data-table department-table p-datatable-striped"
            customStyles={{
              table: {
                style: {
                     borderRight: '1px solid #dee2e6',
      fontWeight: '600',
      fontSize: '14px',
      backgroundColor: '#f8f9fa',
      textAlign: 'center',
                },
              },
              headRow: {
                style: {
                  backgroundColor: "#04364A!important",
                  minHeight: "48px",
                },
              },
              headCells: {
                style: {
                  padding: "10px 14px",
                  fontSize: "15px",
                  fontWeight: "700",
                  color: "white",
                  textTransform: "uppercase",
                },
              },
              rows: {
                style: {
                  borderBottom: "1px solid #fdfdfd",
                  "&:hover": {
                    backgroundColor: "#fdfdfd",
                  },
                },
              },
              cells: {
                style: {
                  padding: "10px 14px",
                  fontSize: "14px",
                  color: "#334155",
                },
              },
            }}
              conditionalRowStyles={[
     {
    when: (row) => row._rowIndex % 2 === 0, 
    style: { backgroundColor: '#f2f2f2' },
  },
  {
    when: (row) => row._rowIndex % 2 !== 0,
    style: { backgroundColor: '#ffffff' },
  },
  ]}
            striped
            responsive
            noDataComponent={<Alert variant="info">No categories found</Alert>}
            progressPending={isLoading}
            progressComponent={<Spinner animation="border" variant="btn-primary" />}
            onRowClicked={(row) => speak(`Category: ${row.categoryName}`)}
          />
        </div>
      )}

      <Modal show={isModalOpen} onHide={closeModal} backdrop="static">
        <Modal.Header closeButton className="modal-customheader">
          <Modal.Title className="custom-modaltitle">{editId ? 'Edit Category' : 'Add Category'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-1">
              <Form.Label className='mb-2'>Category Name</Form.Label>
              <div className="input-group mb-3">
                <Form.Control
                  type="text"
                  ref={inputRef}
                  value={formData.categoryName}
                  onChange={(e) => {
                    const onlyLetters = e.target.value.replace(/[^a-zA-Z\s]/g, '');
                    setFormData({ ...formData, categoryName: onlyLetters });
                  }}
                  placeholder="Enter category name"
                  required
                  disabled={isLoading}
                />
                <Button 
                  variant={isListening ? "danger" : ""} 
                  className='addcategory-button'
                  onClick={handleSpeechInput}
                  disabled={isLoading}
                >
                  <i className={`bi bi-mic${isListening ? "-fill" : ""}`}></i>
                </Button>
              </div>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer className="custommodal-footer pb-3">
            <Button 
              variant="secondary" 
              className="btn-fontweight cancelbtn-fontsize" 
              onClick={closeModal} 
              disabled={isLoading}
              onFocus={() => speak("Cancel button")}
            >
              Cancel
            </Button>
            <Button 
              variant="btn-primary"  
              className="btn-fontweight addbtn-fontsize" 
              type="submit" 
              disabled={isLoading}
              onFocus={() => speak(editId ? "Update category button" : "Add category button")}
            >
              {isLoading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  {editId ? 'Updating...' : 'Adding...'}
                </>
              ) : editId ? 'Update Category' : 'Add Category'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default Category;